/**
 * The printed creative brief: A4 portrait pages with pictures, drawn to the
 * Pen's print frames (08 · ch4: cw4vk and oOzPC for a vertical video, FT5Ri,
 * a5Kv8s and mAlaP for a horizontal one). Print uses the brand print palette
 * (lime #CFFF0A labels on ink #121212, Barlow Condensed labels, Manrope text)
 * and never follows the screen theme.
 *
 * Page 1: letterhead (the Mohtawa mark, the status tag and who it is for), the
 * title and meta line, the key frame (shot 01 with its words on screen) beside
 * or above the treatment (opening hook, sound, length with its timeline, the
 * idea it came from), production inputs, and the post captions when they fit.
 * Then the storyboard, two shots a row, and whatever is left (captions, shoot
 * day, deliverables, legal). Every page closes on the Mohtawa footer.
 *
 * Only what the brief holds is printed. A shot with no picture prints a quiet
 * frame that says so; a web page pasted as a reference is never fetched as an
 * image.
 */
import { GState, jsPDF } from "jspdf";
import barlowCondensedBoldUrl from "@/assets/fonts/BarlowCondensed-Bold.ttf?url";
import jetBrainsMonoMediumUrl from "@/assets/fonts/JetBrainsMono-Medium.ttf?url";
import manropeBoldUrl from "@/assets/fonts/Manrope-Bold.ttf?url";
import manropeRegularUrl from "@/assets/fonts/Manrope-Regular.ttf?url";
import mohtawaMarkUrl from "@/assets/logos/logo.svg?url";
import {
  baseDirection,
  ensureArabicFont,
  hasArabic,
  setArabicFont,
  toVisual,
  wrapBidi,
} from "@/lib/pdfBidi";
import { scrubVendorTermsDeep } from "@/lib/vendorTerms";
import type { CalendarPlanSlot, CalendarSlotIdea } from "@/types/contentCalendar.type";
import { MEDIA_TYPE_LABEL, displayAspectRatio } from "@/types/contentCalendar.type";
import type {
  CalendarBriefStatus,
  DistributionEntry,
  ProductionBriefBody,
  ProductionPanel,
  ProductionStaticBody,
  ProductionVideoBody,
  ResolvedBriefVisual,
  StoryboardScene,
} from "@/types/productionBrief.type";
import { platformLabel } from "../../components/PlatformGlyph";
import {
  cameraLine,
  isEndFrameTitle,
  parsedTotal,
  rangeLabel,
  shotNumber,
  shotTimings,
  staticFormatLine,
  staticSizeLine,
  type ShotTiming,
} from "./brief/briefParts";
import { parseScheduleTiming } from "./productionBriefSchedule";

// ---- Page geometry, in the Pen's px (794 x 1123 for A4) -------------------

const PAGE_PX_W = 794;
const PAGE_PX_H = 1123;
const A4_PT_W = 595.28;
/** Points per Pen px. Every size below is written in Pen px and passed through `p`. */
const S = A4_PT_W / PAGE_PX_W;
const p = (px: number) => px * S;

const PAD_X = 72;
const PAD_TOP = 56;
const PAD_BOTTOM = 36;
const CONTENT_W = PAGE_PX_W - PAD_X * 2;
/** The footer: a 0.75 hairline, 10 above two 8.5 lines. */
const FOOTER_H = 10 + 12 * 2 + 2;
const CONTENT_BOTTOM = PAGE_PX_H - PAD_BOTTOM - FOOTER_H - 16;
const IMAGE_FETCH_TIMEOUT_MS = 5_000;

type RGB = [number, number, number];
const INK: RGB = [18, 18, 18];
const MUTED: RGB = [92, 92, 92];
const LINE: RGB = [212, 212, 212];
const LIME: RGB = [207, 255, 10];
const SEG: RGB = [230, 230, 230];
const SEG_END: RGB = [189, 189, 189];
const QUIET: RGB = [237, 237, 237];
const TAG_INK: RGB = [138, 138, 138];
const WHITE: RGB = [255, 255, 255];

export type SeatBriefMeta = {
  brandName: string;
  preparedBy?: string;
  documentVersion?: number;
};

export type SeatBriefPayload = {
  kind: "VIDEO" | "STATIC";
  status: CalendarBriefStatus;
  body: ProductionBriefBody;
};

type LoadedImage = { dataUrl: string; format: "PNG" | "JPEG" | "WEBP"; alias: string };
type ImageMap = Map<string, LoadedImage | null>;
type Face = "body" | "display" | "mono";

type Ctx = {
  doc: jsPDF;
  brand: string;
  fonts: boolean;
  /** The tag on every letterhead: "Draft · version 2". */
  tag: string;
  /** Under the tag: "Creative brief for Fatafeat". */
  forLine: string;
  mark: LoadedImage | null;
  images: ImageMap;
  /** Cursor on the current page, in Pen px from the top. */
  y: number;
};

// ---- Fonts and text ----------------------------------------------------------

const fontCache = new Map<string, Promise<string>>();
const fontDocs = new WeakSet<jsPDF>();

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

function loadFontBase64(url: string): Promise<string> {
  const cached = fontCache.get(url);
  if (cached) return cached;
  const pending = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Could not load a brief font: ${url}`);
      return response.arrayBuffer();
    })
    .then(arrayBufferToBase64)
    .catch((error) => {
      fontCache.delete(url);
      throw error;
    });
  fontCache.set(url, pending);
  return pending;
}

async function ensureBrandFonts(doc: jsPDF): Promise<boolean> {
  try {
    const definitions = [
      [barlowCondensedBoldUrl, "BarlowCondensed-Bold.ttf", "Barlow Condensed", "bold"],
      [manropeRegularUrl, "Manrope-Regular.ttf", "Manrope", "normal"],
      [manropeBoldUrl, "Manrope-Bold.ttf", "Manrope", "bold"],
      [jetBrainsMonoMediumUrl, "JetBrainsMono-Medium.ttf", "JetBrains Mono", "normal"],
    ] as const;
    const data = await Promise.all(definitions.map(([url]) => loadFontBase64(url)));
    definitions.forEach(([, fileName, family, style], index) => {
      doc.addFileToVFS(fileName, data[index]!);
      doc.addFont(fileName, family, style);
    });
    fontDocs.add(doc);
    return true;
  } catch {
    return false;
  }
}

function setRgb(doc: jsPDF, rgb: RGB, kind: "fill" | "draw" | "text") {
  if (kind === "fill") doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  else if (kind === "draw") doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  else doc.setTextColor(rgb[0], rgb[1], rgb[2]);
}

/** Sets the face for Latin text, or Amiri when the text carries Arabic. */
function setFace(doc: jsPDF, face: Face, bold: boolean, sizePx: number, text = "") {
  if (text && hasArabic(text)) {
    setArabicFont(doc, bold, p(sizePx));
    return;
  }
  if (fontDocs.has(doc)) {
    if (face === "display") doc.setFont("Barlow Condensed", "bold");
    else if (face === "mono") doc.setFont("JetBrains Mono", "normal");
    else doc.setFont("Manrope", bold ? "bold" : "normal");
  } else {
    doc.setFont(face === "mono" ? "courier" : "helvetica", bold || face === "display" ? "bold" : "normal");
  }
  doc.setFontSize(p(sizePx));
}

type Lines = { lines: string[]; rtl: boolean };

function wrap(doc: jsPDF, text: string, widthPx: number, face: Face, bold: boolean, sizePx: number): Lines {
  const value = text.trim();
  if (!value) return { lines: [], rtl: false };
  setFace(doc, face, bold, sizePx, value);
  if (hasArabic(value)) {
    const direction = baseDirection(value);
    return { lines: wrapBidi(doc, value, p(widthPx)).map((line) => toVisual(line, direction)), rtl: direction === "rtl" };
  }
  return { lines: doc.splitTextToSize(value, p(widthPx)) as string[], rtl: false };
}

/**
 * Draws wrapped text in a box whose first line starts at `top` (Pen px), each
 * line `size * lh` tall with the glyphs centred in it, as the Pen sets text.
 * Returns the bottom of the last line.
 */
function drawText(
  doc: jsPDF,
  text: string,
  x: number,
  top: number,
  widthPx: number,
  opts: { size: number; lh?: number; face?: Face; bold?: boolean; color?: RGB; align?: "left" | "center" | "right"; maxLines?: number; charSpace?: number },
): number {
  const { size, lh = 1.4, face = "body", bold = false, color = INK, maxLines, charSpace } = opts;
  const wrapped = wrap(doc, text, widthPx, face, bold, size);
  let lines = wrapped.lines;
  if (maxLines && lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    lines[maxLines - 1] = `${lines[maxLines - 1]!.replace(/\s*\S{0,3}$/, "")}…`;
  }
  const lineH = size * lh;
  setFace(doc, face, bold, size, text);
  setRgb(doc, color, "text");
  const align = opts.align ?? (wrapped.rtl ? "right" : "left");
  lines.forEach((line, index) => {
    const baseline = top + index * lineH + (lineH - size) / 2 + size * 0.8;
    const ax = align === "right" ? x + widthPx : align === "center" ? x + widthPx / 2 : x;
    doc.text(line, p(ax), p(baseline), {
      align,
      ...(charSpace ? { charSpace: p(charSpace) } : {}),
    });
  });
  return top + lines.length * lineH;
}

function measureText(doc: jsPDF, text: string, widthPx: number, size: number, lh = 1.4, face: Face = "body", bold = false, maxLines?: number): number {
  const count = wrap(doc, text, widthPx, face, bold, size).lines.length;
  return (maxLines ? Math.min(count, maxLines) : count) * size * lh;
}

function textWidthPx(doc: jsPDF, text: string, face: Face, bold: boolean, size: number, charSpace = 0): number {
  setFace(doc, face, bold, size, text);
  return doc.getTextWidth(text) / S + charSpace * Math.max(0, [...text].length - 1);
}

// ---- Shapes -------------------------------------------------------------------

function fillRect(doc: jsPDF, x: number, y: number, w: number, h: number, color: RGB, radius = 0) {
  setRgb(doc, color, "fill");
  if (radius) doc.roundedRect(p(x), p(y), p(w), p(h), p(radius), p(radius), "F");
  else doc.rect(p(x), p(y), p(w), p(h), "F");
}

function hairline(doc: jsPDF, x: number, y: number, w: number, weight = 0.75) {
  setRgb(doc, LINE, "draw");
  doc.setLineWidth(p(weight));
  doc.line(p(x), p(y), p(x + w), p(y));
}

/** Print / Label: the lime section tag, Barlow 13 caps at 0.6 tracking, 1 by 7 padding. */
function drawLabel(doc: jsPDF, text: string, x: number, top: number): number {
  const label = text.toUpperCase();
  const size = 13;
  const h = size * 1.2 + 2;
  const w = textWidthPx(doc, label, "display", true, size, 0.6) + 14;
  fillRect(doc, x, top, w, h, LIME);
  drawText(doc, label, x + 7, top + 1, w, { size, lh: 1.2, face: "display", color: INK, charSpace: 0.6 });
  return top + h;
}

/** Print / Duration: "4 s" on lime (the first shot) or the quiet fill. */
function drawDuration(doc: jsPDF, text: string, right: number, centerY: number, lit: boolean) {
  const size = 9;
  const h = size * 1.3 + 2;
  const w = textWidthPx(doc, text, "body", true, size) + 10;
  fillRect(doc, right - w, centerY - h / 2, w, h, lit ? LIME : QUIET);
  drawText(doc, text, right - w + 5, centerY - h / 2 + 1, w, { size, lh: 1.3, bold: true });
  return w;
}

// ---- Images -------------------------------------------------------------------

function resolveDirectImageUrl(raw?: string): string | null {
  const value = raw?.trim();
  if (!value) return null;
  if (/^data:image\/(?:png|jpe?g|webp);base64,/i.test(value)) return value;
  if (/^blob:/i.test(value)) return value;
  try {
    const browserBase = typeof window !== "undefined" ? window.location.href : undefined;
    if (!browserBase && !/^https?:\/\//i.test(value)) return null;
    const url = new URL(value, browserBase);
    if (!/^https?:$/.test(url.protocol) || !/\.(?:png|jpe?g|webp)$/i.test(url.pathname)) return null;
    if (!/^https?:\/\//i.test(value) && typeof window !== "undefined" && url.origin !== window.location.origin) {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

function formatForDataUrl(dataUrl: string): LoadedImage["format"] | null {
  const type = dataUrl.match(/^data:image\/([^;,]+)/i)?.[1]?.toLowerCase();
  if (type === "png") return "PNG";
  if (type === "jpg" || type === "jpeg") return "JPEG";
  if (type === "webp") return "WEBP";
  return null;
}

function imageAlias(raw: string): string {
  let hash = 2166136261;
  for (let index = 0; index < raw.length; index += 1) {
    hash ^= raw.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `brief-image-${(hash >>> 0).toString(16)}`;
}

/**
 * `trusted` skips only the filename check, which stops us fetching a web page
 * somebody pasted as a reference. A picked visual's URL is signed by our own
 * backend; the response's content-type is still checked, so nothing but an
 * image is ever drawn.
 */
/**
 * Re-encodes an image through a canvas as JPEG on white, at most 1600 px on its
 * long side, so progressive or CMYK files jsPDF cannot parse still print. Outside a browser the source is used as it is.
 */
async function normalise(dataUrl: string): Promise<string> {
  if (typeof document === "undefined" || typeof Image === "undefined") return dataUrl;
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const longest = Math.max(image.naturalWidth, image.naturalHeight) || 1;
      const scale = Math.min(1, 1600 / longest);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) {
        resolve(dataUrl);
        return;
      }
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      try {
        resolve(canvas.toDataURL("image/jpeg", 0.88));
      } catch {
        resolve(dataUrl);
      }
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });
}

async function loadDirectImage(raw?: string, trusted = false): Promise<LoadedImage | null> {
  const resolved = trusted ? raw?.trim() || null : resolveDirectImageUrl(raw);
  if (!resolved) return null;
  try {
    if (resolved.startsWith("data:")) {
      const dataUrl = await normalise(resolved);
      const format = formatForDataUrl(dataUrl);
      return format ? { dataUrl, format, alias: imageAlias(resolved) } : null;
    }
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS);
    let response: Response;
    try {
      response = await fetch(resolved, { signal: controller.signal });
    } finally {
      globalThis.clearTimeout(timeout);
    }
    if (!response.ok) return null;
    const mime = response.headers.get("content-type")?.split(";")[0]?.toLowerCase() ?? "";
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(mime)) return null;
    const dataUrl = await normalise(`data:${mime === "image/jpg" ? "image/jpeg" : mime};base64,${arrayBufferToBase64(await response.arrayBuffer())}`);
    const format = formatForDataUrl(dataUrl);
    return format ? { dataUrl, format, alias: imageAlias(resolved) } : null;
  } catch {
    return null;
  }
}

/** Logo / Mohtawa mark (src/assets/logos/logo.svg), rasterised for paper. */
async function loadMark(): Promise<LoadedImage | null> {
  if (typeof document === "undefined" || typeof Image === "undefined") return null;
  try {
    const svg = await (await fetch(mohtawaMarkUrl)).text();
    const blobUrl = URL.createObjectURL(new Blob([svg.replace(/fill=(['"])none\1/, 'fill="#121212"')], { type: "image/svg+xml" }));
    try {
      const image = new Image();
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error("mark"));
        image.src = blobUrl;
      });
      const canvas = document.createElement("canvas");
      canvas.width = 416;
      canvas.height = 416;
      const context = canvas.getContext("2d");
      if (!context) return null;
      context.drawImage(image, 0, 0, 416, 416);
      return { dataUrl: canvas.toDataURL("image/png"), format: "PNG", alias: "mohtawa-mark" };
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch {
    return null;
  }
}

function pickedUrl(row: { resolvedVisual?: ResolvedBriefVisual }): string | null {
  const picked = row.resolvedVisual;
  if (!picked || picked.unavailable) return null;
  // A video pick prints its still; there is no other frame to put on paper.
  return picked.imageUrl ?? picked.thumbnailUrl ?? picked.items?.[0]?.posterUrl ?? null;
}

async function loadBriefImages(brief: SeatBriefPayload | null | undefined): Promise<ImageMap> {
  const result: ImageMap = new Map();
  if (!brief) return result;
  const rows: Array<{ resolvedVisual?: ResolvedBriefVisual; referenceUrl?: string }> =
    brief.kind === "VIDEO"
      ? ((brief.body as ProductionVideoBody).storyboard ?? [])
      : ((brief.body as ProductionStaticBody).panels ?? []);
  const byUrl = new Map<string, Promise<LoadedImage | null>>();
  await Promise.all(
    rows.map(async (row, index) => {
      const picked = pickedUrl(row);
      const source = picked ?? row.referenceUrl;
      if (!source) {
        result.set(String(index), null);
        return;
      }
      const pending = byUrl.get(source) ?? loadDirectImage(source, !!picked);
      byUrl.set(source, pending);
      result.set(String(index), await pending);
    }),
  );
  return result;
}

/**
 * Print / Shot picture: the image covering a radius 12 frame, the words on
 * screen in a dark band at its foot. No image: a quiet frame that says so.
 */
function drawPicture(
  doc: jsPDF,
  image: LoadedImage | null | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
  overlay?: { text: string; size: number; padY: number; padX: number; inset: number; fit: boolean },
) {
  const r = 12;
  let drawn = false;
  if (image) {
    try {
      const props = doc.getImageProperties(image.dataUrl);
      const scale = Math.max(w / props.width, h / props.height);
      const dw = props.width * scale;
      const dh = props.height * scale;
      doc.saveGraphicsState();
      doc.roundedRect(p(x), p(y), p(w), p(h), p(r), p(r), null);
      doc.clip();
      doc.discardPath();
      doc.addImage(image.dataUrl, image.format, p(x + (w - dw) / 2), p(y + (h - dh) / 2), p(dw), p(dh), image.alias, "MEDIUM");
      doc.restoreGraphicsState();
      drawn = true;
    } catch {
      drawn = false;
    }
  }
  if (!drawn) {
    fillRect(doc, x, y, w, h, QUIET, r);
    drawText(doc, "No picture yet", x, y + h / 2 - 7, w, { size: 9, color: MUTED, align: "center" });
  }
  const words = overlay?.text.trim();
  if (overlay && words) {
    const inner = w - overlay.inset * 2;
    const textW = overlay.fit
      ? Math.min(inner - overlay.padX * 2, textWidthPx(doc, words, "body", true, overlay.size) + 1)
      : inner - overlay.padX * 2;
    const textH = measureText(doc, words, textW, overlay.size, 1.2, "body", true, 2);
    const bandW = textW + overlay.padX * 2;
    const bandH = textH + overlay.padY * 2;
    const bandX = x + (w - bandW) / 2;
    const bandY = y + h - overlay.inset - bandH;
    doc.saveGraphicsState();
    doc.setGState(new GState({ opacity: 0.72 }));
    fillRect(doc, bandX, bandY, bandW, bandH, INK);
    doc.restoreGraphicsState();
    drawText(doc, words, bandX + overlay.padX, bandY + overlay.padY, textW, {
      size: overlay.size,
      lh: 1.2,
      bold: true,
      color: WHITE,
      align: "center",
      maxLines: 2,
    });
  }
}

// ---- Page chrome --------------------------------------------------------------

function drawLetterhead(ctx: Ctx) {
  const { doc } = ctx;
  fillRect(doc, 0, 0, PAGE_PX_W, PAGE_PX_H, WHITE);
  if (ctx.mark) {
    doc.addImage(ctx.mark.dataUrl, ctx.mark.format, p(PAD_X), p(PAD_TOP), p(52), p(52), ctx.mark.alias, "FAST");
  } else {
    drawText(doc, "Mohtawa", PAD_X, PAD_TOP, 200, { size: 16, face: "display", lh: 1.2 });
  }
  // Tag / Default: radius 6, 24 tall, 8 in, JetBrains Mono 11 at 1.32 tracking.
  const right = PAD_X + CONTENT_W;
  const tagW = textWidthPx(doc, ctx.tag, "mono", false, 11, 1.32) + 16;
  fillRect(doc, right - tagW, PAD_TOP, tagW, 24, QUIET, 6);
  drawText(doc, ctx.tag, right - tagW + 8, PAD_TOP + 5, tagW, { size: 11, lh: 1.25, face: "mono", color: TAG_INK, charSpace: 1.32 });
  drawText(doc, ctx.forLine, right - 300, PAD_TOP + 30, 300, { size: 9.5, color: MUTED, align: "right" });
  ctx.y = PAD_TOP + 52;
}

function drawFooter(doc: jsPDF, page: number, count: number) {
  const top = PAGE_PX_H - PAD_BOTTOM - FOOTER_H;
  hairline(doc, PAD_X, top, CONTENT_W);
  drawText(doc, "www.mo7tawa.me · +971 (0)4 552 1883", PAD_X, top + 10, 500, { size: 8.5, color: MUTED });
  drawText(doc, "Meydan Grandstand, 6th Floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E.", PAD_X, top + 24, 500, { size: 8.5, color: MUTED });
  drawText(doc, `Page ${page} of ${count}`, PAD_X + CONTENT_W - 200, top + 24, 200, { size: 8.5, color: MUTED, align: "right" });
}

function newPage(ctx: Ctx) {
  ctx.doc.addPage("a4", "portrait");
  drawLetterhead(ctx);
}

/** Starts a new page when `height` does not fit under the cursor. */
function ensureRoom(ctx: Ctx, height: number) {
  if (ctx.y + height > CONTENT_BOTTOM) newPage(ctx);
}

// ---- Content helpers ------------------------------------------------------------

const clean = (value?: string | null) => value?.trim() ?? "";
const cleanList = (items?: Array<string | null | undefined>) =>
  (items ?? []).map((item) => item?.trim() ?? "").filter(Boolean);

function statusTag(status: CalendarBriefStatus | null, version?: number): string {
  const word: Record<CalendarBriefStatus, string> = {
    GENERATING: "Being written",
    DRAFT: "Draft",
    IN_REVIEW: "With the client",
    CHANGES_REQUESTED: "Changes asked",
    ACCEPTED: "Approved",
    FAILED: "Not written",
  };
  const head = status ? word[status] : "Approved idea";
  return version ? `${head} · version ${version}` : head;
}

function dayLabel(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).replace(/,/g, "");
}

/** "51 seconds" under a minute; "2 min 30 s" from a minute up. */
function lengthWords(seconds: number): string {
  const total = Math.round(seconds);
  if (total < 60) return `${total} seconds`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s ? `${m} min ${s} s` : `${m} min`;
}

function shortLength(seconds: number): string {
  const total = Math.round(seconds);
  if (total < 60) return `${total} s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s ? `${m} min ${s} s` : `${m} min`;
}

function isLandscape(ratio: string | null | undefined): boolean {
  const match = /^\s*(\d+(?:\.\d+)?)\s*[:x×/]\s*(\d+(?:\.\d+)?)\s*$/i.exec(ratio ?? "");
  return !!match && Number(match[1]) > Number(match[2]);
}

function ratioOf(value: string | null | undefined, fallback: [number, number]): [number, number] {
  const match = /^\s*(\d+(?:\.\d+)?)\s*[:x×/]\s*(\d+(?:\.\d+)?)\s*$/i.exec(value ?? "");
  return match ? [Number(match[1]), Number(match[2])] : fallback;
}

// ---- Sections ------------------------------------------------------------------

type Segment = { label: string; seconds: number };

/** The lime first shot, quiet middle shots and a darker end frame, 1.5 apart. */
function drawTimeline(doc: jsPDF, segments: Segment[], x: number, top: number, width: number, height: number, size: number) {
  const total = segments.reduce((sum, segment) => sum + segment.seconds, 0);
  if (!total) return top;
  const gap = 1.5;
  const usable = width - gap * (segments.length - 1);
  let cursor = x;
  segments.forEach((segment, index) => {
    const w = (segment.seconds / total) * usable;
    const color = index === 0 ? LIME : index === segments.length - 1 && segment.label === "End" ? SEG_END : SEG;
    fillRect(doc, cursor, top, w, height, color);
    if (textWidthPx(doc, segment.label, "body", true, size) + 6 <= w) {
      drawText(doc, segment.label, cursor + 4, top + (height - size) / 2, w - 4, { size, lh: 1, bold: true });
    }
    cursor += w + gap;
  });
  return top + height;
}

type Block = { label: string; text?: string; hook?: boolean; timeline?: Segment[] };

function drawBlock(doc: jsPDF, block: Block, x: number, top: number, width: number, hookSize: number, bodySize: number, gap: number): number {
  let y = drawLabel(doc, block.label, x, top) + gap;
  if (block.text) {
    y = block.hook
      ? drawText(doc, block.text, x, y, width, { size: hookSize, lh: 1.15, face: "display", bold: true })
      : drawText(doc, block.text, x, y, width, { size: bodySize, lh: 1.4 });
  }
  if (block.timeline?.length) y = drawTimeline(doc, block.timeline, x, y + 6, width, 16, 7.5);
  return y;
}

/** Production inputs: up to four columns of lime-bulleted lists, 18 apart. */
function inputGroups(body: ProductionBriefBody): Array<{ title: string; items: string[] }> {
  return [
    { title: "Props and products", items: cleanList(body.props) },
    { title: "Wardrobe", items: cleanList(body.wardrobe) },
    { title: "Location", items: cleanList(body.locations) },
    { title: "Talent", items: cleanList(body.talent) },
    { title: "Crew", items: cleanList(body.crew) },
  ].filter((group) => group.items.length);
}

function listHeight(doc: jsPDF, items: string[], width: number): number {
  return items.reduce((sum, item) => sum + measureText(doc, item, width - 11, 10) + 6, 0);
}

function drawList(doc: jsPDF, items: string[], x: number, top: number, width: number): number {
  let y = top;
  items.forEach((item) => {
    // Print / List item: a 4px lime square with an ink edge, 7 before the line.
    setRgb(doc, LIME, "fill");
    setRgb(doc, INK, "draw");
    doc.setLineWidth(p(0.5));
    doc.rect(p(x), p(y + 5), p(4), p(4), "FD");
    y = drawText(doc, item, x + 11, y, width - 11, { size: 10 }) + 6;
  });
  return y - 6;
}

function writeInputs(ctx: Ctx, body: ProductionBriefBody, padTop: number) {
  const { doc } = ctx;
  const groups = inputGroups(body);
  if (!groups.length) return;
  const columns = Math.min(4, groups.length);
  const colW = (CONTENT_W - 18 * (columns - 1)) / columns;
  const rows: Array<typeof groups> = [];
  for (let index = 0; index < groups.length; index += columns) rows.push(groups.slice(index, index + columns));
  const rowHeight = (row: typeof groups) =>
    Math.max(...row.map((group) => 14 + 6 + listHeight(doc, group.items, colW)));
  ensureRoom(ctx, padTop + 18 + 12 + rowHeight(rows[0]!));
  let y = drawLabel(doc, "Production inputs", PAD_X, ctx.y + padTop) + 12;
  rows.forEach((row, rowIndex) => {
    if (rowIndex) {
      if (y + rowHeight(row) > CONTENT_BOTTOM) {
        newPage(ctx);
        y = ctx.y + 26;
      } else y += 14;
    }
    row.forEach((group, index) => {
      const x = PAD_X + index * (colW + 18);
      const after = drawText(doc, group.title, x, y, colW, { size: 10, bold: true }) + 6;
      drawList(doc, group.items, x, after, colW);
    });
    y += rowHeight(row);
  });
  ctx.y = y;
}

type Caption = { channel: string; label: string | null; text: string };

function captionsOf(body: ProductionBriefBody, kind: "VIDEO" | "STATIC"): Caption[] {
  const entries = Object.entries(body.distribution ?? {}).filter(([, entry]: [string, DistributionEntry]) =>
    clean(entry.caption) || clean(entry.title) || clean(entry.description),
  );
  const list: Caption[] = entries.map(([platform, entry]) => {
    if (platform === "YOUTUBE") {
      return {
        channel: platformLabel(platform),
        label: "Title and description",
        text: [clean(entry.title), clean(entry.description)].filter(Boolean).join("\n\n"),
      };
    }
    return { channel: platformLabel(platform), label: null, text: clean(entry.caption) };
  });
  if (!list.length && kind === "STATIC") {
    const design = body as ProductionStaticBody;
    if (clean(design.caption)) {
      list.push({ channel: "Caption", label: null, text: clean(design.caption) });
    }
  }
  return list;
}

function captionCardHeight(doc: jsPDF, caption: Caption, width: number): number {
  const inner = width - 24;
  let h = 10 + 10.5 * 1.4 + 5;
  if (caption.label) h += 8.5 * 1.4 + 5;
  for (const paragraph of captionParagraphs(caption)) h += measureText(doc, paragraph, inner, 10) + 5;
  return h + 5;
}

function drawCaptionCard(doc: jsPDF, caption: Caption, x: number, top: number, width: number, height: number) {
  setRgb(doc, LINE, "draw");
  doc.setLineWidth(p(0.75));
  doc.rect(p(x), p(top), p(width), p(height), "S");
  const inner = width - 24;
  let y = drawText(doc, caption.channel, x + 12, top + 10, inner, { size: 10.5, bold: true }) + 5;
  if (caption.label) y = drawText(doc, caption.label, x + 12, y, inner, { size: 8.5, color: MUTED }) + 5;
  for (const paragraph of captionParagraphs(caption)) {
    y = drawText(doc, paragraph, x + 12, y, inner, { size: 10, color: caption.text ? INK : MUTED }) + 5;
  }
}

/**
 * A multilingual caption carries one language per paragraph. Drawn one
 * paragraph at a time so each takes its own direction and face.
 */
function captionParagraphs(caption: Caption): string[] {
  if (!caption.text) return ["Not written yet"];
  return caption.text.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
}

function captionsHeight(doc: jsPDF, captions: Caption[], padTop: number): number {
  const colW = (CONTENT_W - 16) / 2;
  let h = padTop + 13 * 1.2 + 2 + 12;
  for (let index = 0; index < captions.length; index += 2) {
    const pair = captions.slice(index, index + 2);
    h += Math.max(...pair.map((caption) => captionCardHeight(doc, caption, colW))) + (index ? 16 : 0);
  }
  return h;
}

function writeCaptions(ctx: Ctx, captions: Caption[], padTop: number) {
  const { doc } = ctx;
  if (!captions.length) return;
  const colW = (CONTENT_W - 16) / 2;
  const firstPair = captions.slice(0, 2);
  ensureRoom(ctx, padTop + 18 + 12 + Math.max(...firstPair.map((caption) => captionCardHeight(doc, caption, colW))));
  let y = drawLabel(doc, "Post captions", PAD_X, ctx.y + padTop) + 12;
  for (let index = 0; index < captions.length; index += 2) {
    const pair = captions.slice(index, index + 2);
    const h = Math.max(...pair.map((caption) => captionCardHeight(doc, caption, colW)));
    if (index) {
      if (y + 16 + h > CONTENT_BOTTOM) {
        newPage(ctx);
        y = drawLabel(doc, "Post captions", PAD_X, ctx.y + 26) + 12;
      } else y += 16;
    }
    pair.forEach((caption, column) => drawCaptionCard(doc, caption, PAD_X + column * (colW + 16), y, colW, h));
    y += h;
  }
  ctx.y = y;
}

// ---- Storyboard ------------------------------------------------------------------

type Card = {
  number: string;
  title: string;
  range: string | null;
  duration: string | null;
  action: string;
  onScreen: string;
  lanes: Array<{ label: string; value: string; strong?: boolean }>;
  image: LoadedImage | null | undefined;
};

function shotCards(body: ProductionVideoBody, images: ImageMap): Card[] {
  const shots = body.storyboard ?? [];
  const timings = shotTimings(shots.map((shot) => shot.duration));
  return shots.map((scene: StoryboardScene, index) => {
    const end = index === shots.length - 1 && index > 0 && isEndFrameTitle(scene.title);
    const timing: ShotTiming = timings[index]!;
    const visual = clean(scene.visual);
    const action = clean(scene.action);
    const description = !action || visual.toLowerCase().includes(action.toLowerCase())
      ? visual
      : visual
        ? `${/[.!?]$/.test(visual) ? visual : `${visual}.`} ${action}`
        : action;
    return {
      number: end ? "End" : shotNumber(index),
      title: clean(scene.title) || `Shot ${shotNumber(index)}`,
      range: rangeLabel(timing),
      duration: timing.seconds !== null ? `${Math.round(timing.seconds * 10) / 10} s` : clean(scene.duration) || null,
      action: description,
      onScreen: clean(scene.textOnScreen),
      lanes: [
        { label: "Voiceover", value: clean(scene.voiceover) },
        { label: "SFX", value: clean(scene.sound) },
        { label: "On screen", value: clean(scene.textOnScreen), strong: true },
        { label: "Camera", value: cameraLine([scene.framing, scene.shotSize, scene.cameraAngle, scene.cameraMovement, scene.lens]) },
      ],
      image: images.get(String(index)),
    };
  });
}

function slideCards(body: ProductionStaticBody, images: ImageMap): Card[] {
  return (body.panels ?? []).map((panel: ProductionPanel, index) => ({
    number: String(index + 1),
    title: clean(panel.role) || `Slide ${index + 1}`,
    range: clean(panel.aspectRatio) || null,
    duration: null,
    action: clean(panel.composition),
    onScreen: clean(panel.copyOnPanel),
    lanes: [
      { label: "Subject", value: clean(panel.subject) },
      { label: "On the slide", value: clean(panel.copyOnPanel), strong: true },
      { label: "How it is made", value: clean(panel.imageryNote) },
      { label: "Assets", value: clean(panel.assets) },
    ],
    image: images.get(String(index)),
  }));
}

/** Vertical cards: the picture beside its notes, lanes stacked label over value. */
function stackedCardHeight(doc: jsPDF, card: Card, notesW: number, picH: number): number {
  let h = 20 + 6 + 8 * 1.4 + 6 + 10 * 1.4 + 6;
  if (card.action) h += measureText(doc, card.action, notesW, 9) + 6;
  h += 6 + card.lanes.reduce((sum, lane) => sum + 8.5 * 1.2 + 1 + measureText(doc, lane.value || "None", notesW, 8.5, 1.35) + 4, -4);
  return Math.max(picH, h);
}

function drawStackedCard(doc: jsPDF, card: Card, x: number, top: number, width: number, picW: number, picH: number, lit: boolean) {
  drawPicture(doc, card.image, x, top, picW, picH, card.onScreen ? { text: card.onScreen, size: 8, padX: 5, padY: 3, inset: 7, fit: false } : undefined);
  const nx = x + picW + 12;
  const nw = width - picW - 12;
  drawText(doc, card.number, nx, top, nw, { size: 20, lh: 1, face: "display", bold: true });
  if (card.duration) drawDuration(doc, card.duration, nx + nw, top + 10, lit);
  let y = top + 20 + 6;
  if (card.range) y = drawText(doc, card.range, nx, y, nw, { size: 8, color: MUTED }) + 6;
  y = drawText(doc, card.title, nx, y, nw, { size: 10, bold: true }) + 6;
  if (card.action) y = drawText(doc, card.action, nx, y, nw, { size: 9 }) + 6;
  hairline(doc, nx, y, nw, 0.5);
  y += 6;
  card.lanes.forEach((lane, index) => {
    if (index) y += 4;
    y = drawText(doc, lane.label.toUpperCase(), nx, y, nw, { size: 8.5, lh: 1.2, face: "display", charSpace: 0.5 }) + 1;
    y = drawText(doc, lane.value || "None", nx, y, nw, { size: 8.5, lh: 1.35, bold: !!lane.value && lane.strong, color: lane.value ? INK : MUTED });
  });
}

/** Horizontal cards: the 16:9 picture full width, then the number line and lanes in two columns. */
function wideCardHeight(doc: jsPDF, card: Card, width: number, picH: number): number {
  let h = picH + 7 + 18 + 7;
  if (card.action) h += measureText(doc, card.action, width, 9) + 7;
  h += 5 + card.lanes.reduce((sum, lane) => sum + measureText(doc, lane.value || "None", width - 70, 8.5, 1.35) + 3, -3);
  return h;
}

function drawWideCard(doc: jsPDF, card: Card, x: number, top: number, width: number, picH: number, lit: boolean) {
  drawPicture(doc, card.image, x, top, width, picH, card.onScreen ? { text: card.onScreen, size: 8.5, padX: 6, padY: 3, inset: 8, fit: true } : undefined);
  let y = top + picH + 7;
  const numberW = textWidthPx(doc, card.number, "display", true, 18);
  drawText(doc, card.number, x, y, numberW + 2, { size: 18, lh: 1, face: "display", bold: true });
  let right = x + width;
  if (card.duration) right -= drawDuration(doc, card.duration, right, y + 9, lit) + 6;
  if (card.range) {
    const rw = textWidthPx(doc, card.range, "body", false, 8);
    drawText(doc, card.range, right - rw, y + 9 - 5.6, rw + 1, { size: 8, color: MUTED });
    right -= rw + 6;
  }
  drawText(doc, card.title, x + numberW + 6, y + 9 - 7, Math.max(40, right - x - numberW - 6), { size: 10, bold: true, maxLines: 1 });
  y += 18 + 7;
  if (card.action) y = drawText(doc, card.action, x, y, width, { size: 9 }) + 7;
  hairline(doc, x, y, width, 0.5);
  y += 5;
  card.lanes.forEach((lane, index) => {
    if (index) y += 3;
    drawText(doc, lane.label.toUpperCase(), x, y, 62, { size: 8.5, lh: 1.35, face: "display", charSpace: 0.5 });
    y = drawText(doc, lane.value || "None", x + 70, y, width - 70, { size: 8.5, lh: 1.35, bold: !!lane.value && lane.strong, color: lane.value ? INK : MUTED });
  });
}

function writeStoryboard(ctx: Ctx, cards: Card[], wide: boolean, ratio: [number, number], summary: string) {
  const { doc } = ctx;
  if (!cards.length) return;
  const colW = (CONTENT_W - 16) / 2;
  const picW = wide ? colW : 120;
  const picH = wide ? (colW * ratio[1]) / ratio[0] : (120 * ratio[1]) / ratio[0];
  const height = (card: Card) =>
    wide ? wideCardHeight(doc, card, colW, picH) : stackedCardHeight(doc, card, colW - picW - 12, picH);
  const rows: Card[][] = [];
  for (let index = 0; index < cards.length; index += 2) rows.push(cards.slice(index, index + 2));
  const rowPad = wide ? [12, 4] : [14, 14];
  const rowH = (row: Card[]) => rowPad[0]! + Math.max(...row.map(height)) + rowPad[1]!;

  // Group rows into pages so each head can say which shots it holds.
  let rowIndex = 0;
  let first = true;
  while (rowIndex < rows.length) {
    const headH = (first ? 30 : 26) + 13 * 1.2 + 2 + 14;
    if (!first || ctx.y + headH + rowH(rows[rowIndex]!) > CONTENT_BOTTOM) {
      if (!(first && ctx.y === PAD_TOP + 52)) newPage(ctx);
    }
    first = false;
    let y = ctx.y + headH;
    const pageRows: Card[][] = [];
    while (rowIndex < rows.length && (pageRows.length === 0 || y + rowH(rows[rowIndex]!) <= CONTENT_BOTTOM)) {
      pageRows.push(rows[rowIndex]!);
      y += rowH(rows[rowIndex]!);
      rowIndex += 1;
    }
    const onPage = pageRows.flat();
    const partial = onPage.length < cards.length;
    const noun = wide || cards[0]?.duration !== null ? "Shots" : "Slides";
    const count = partial ? `${noun} ${onPage[0]!.number} to ${onPage[onPage.length - 1]!.number} · ${summary}` : summary;
    const labelTop = ctx.y + (ctx.y === PAD_TOP + 52 ? 26 : 30);
    drawLabel(doc, noun === "Slides" ? "Slides" : "Storyboard", PAD_X, labelTop);
    drawText(doc, count, PAD_X + CONTENT_W - 360, labelTop + 2, 360, { size: 9.5, color: MUTED, align: "right" });
    let rowTop = labelTop + 13 * 1.2 + 2 + 14;
    pageRows.forEach((row) => {
      hairline(doc, PAD_X, rowTop, CONTENT_W);
      const cardTop = rowTop + rowPad[0]!;
      row.forEach((card, column) => {
        const x = PAD_X + column * (colW + 16);
        const lit = card === cards[0];
        if (wide) drawWideCard(doc, card, x, cardTop, colW, picH, lit);
        else drawStackedCard(doc, card, x, cardTop, colW, picW, picH, lit);
      });
      rowTop += rowH(row);
    });
    ctx.y = rowTop;
  }
}

// ---- Closing sections (only when the brief fills them) ---------------------------

function writeSimpleList(ctx: Ctx, label: string, items: string[]) {
  const { doc } = ctx;
  if (!items.length) return;
  ensureRoom(ctx, 26 + 18 + 12 + 20);
  let y = drawLabel(doc, label, PAD_X, ctx.y + 26) + 12;
  items.forEach((item) => {
    const h = measureText(doc, item, CONTENT_W - 11, 10) + 6;
    if (y + h > CONTENT_BOTTOM) {
      newPage(ctx);
      y = drawLabel(doc, label, PAD_X, ctx.y + 26) + 12;
    }
    y = drawList(doc, [item], PAD_X, y, CONTENT_W) + 6;
  });
  ctx.y = y - 6;
}

function writeClosing(ctx: Ctx, body: ProductionBriefBody) {
  const schedule = (body.schedule ?? [])
    .map((item) => {
      const timing = parseScheduleTiming(item);
      const when = timing.start ? `${timing.start}${timing.end ? ` to ${timing.end}` : ""}` : timing.legacyNote;
      return [when, clean(item.activity), clean(item.owner)].filter(Boolean).join(" · ");
    })
    .filter(Boolean);
  writeSimpleList(ctx, "Shoot day", schedule);
  const deliverables = (body.deliverables ?? [])
    .map((row) =>
      [clean(row.channel), clean(row.asset), clean(row.aspectRatio), clean(row.dimensions), clean(row.durationOrPages), clean(row.fileFormat), clean(row.notes)]
        .filter(Boolean)
        .join(" · "),
    )
    .filter(Boolean);
  writeSimpleList(ctx, "Deliverables", deliverables);
  writeSimpleList(ctx, "Legal, before it goes out", cleanList(body.legalMandatories));
}

// ---- Page 1 ----------------------------------------------------------------------

function writeTitle(ctx: Ctx, title: string, meta: string, padTop: number, padBottom: number, size: number) {
  const { doc } = ctx;
  let y = ctx.y + padTop;
  y = drawText(doc, title, PAD_X, y, CONTENT_W, { size, lh: 1.02, face: "display", bold: true, maxLines: 3 });
  if (meta) y = drawText(doc, meta, PAD_X, y + 8, CONTENT_W, { size: 10.5, color: MUTED });
  ctx.y = y + padBottom;
}

/** Vertical video and static: the key frame at 200 beside the treatment. */
function writeHeroBeside(ctx: Ctx, image: LoadedImage | null | undefined, onScreen: string, ratio: [number, number], caption: string, blocks: Block[]) {
  const { doc } = ctx;
  const picW = 200;
  const picH = Math.min(356, (picW * ratio[1]) / ratio[0]);
  const textX = PAD_X + picW + 28;
  const textW = CONTENT_W - picW - 28;
  const top = ctx.y;
  drawPicture(doc, image, PAD_X, top, picW, picH, onScreen ? { text: onScreen, size: 13, padX: 8, padY: 6, inset: 12, fit: false } : undefined);
  const captionBottom = caption ? drawText(doc, caption, PAD_X, top + picH + 6, picW, { size: 8.5, color: MUTED }) : top + picH;
  let y = top;
  blocks.forEach((block, index) => {
    if (index) y += 22;
    y = drawBlock(doc, block, textX, y, textW, 21, 10.5, 8);
  });
  ctx.y = Math.max(captionBottom, y);
}

/** Horizontal video: the key frame across the page, hook beside sound and length under it. */
function writeHeroAcross(ctx: Ctx, image: LoadedImage | null | undefined, onScreen: string, ratio: [number, number], caption: string, hook: Block | null, side: Block[]) {
  const { doc } = ctx;
  const picH = (CONTENT_W * ratio[1]) / ratio[0];
  const top = ctx.y;
  drawPicture(doc, image, PAD_X, top, CONTENT_W, picH, onScreen ? { text: onScreen, size: 14, padX: 10, padY: 6, inset: 14, fit: true } : undefined);
  let y = top + picH;
  if (caption) y = drawText(doc, caption, PAD_X, y + 6, CONTENT_W, { size: 8.5, color: MUTED });
  y += 22;
  const hookW = hook && side.length ? 330 : CONTENT_W;
  let left = y;
  if (hook) left = drawBlock(doc, hook, PAD_X, y, hookW, 17, 10, 8);
  let right = y;
  const sideX = hook ? PAD_X + hookW + 28 : PAD_X;
  const sideW = hook ? CONTENT_W - hookW - 28 : CONTENT_W;
  side.forEach((block, index) => {
    if (index) right += 14;
    right = drawBlock(doc, block, sideX, right, sideW, 17, 10, 6);
  });
  ctx.y = Math.max(left, right);
}

// ---- Build -------------------------------------------------------------------------

/** Build a print-ready document without triggering a download. */
export async function buildSeatBriefPdf(
  rawSlot: CalendarPlanSlot,
  rawIdea: CalendarSlotIdea,
  rawMeta: SeatBriefMeta,
  rawBrief?: SeatBriefPayload | null,
): Promise<{ doc: jsPDF; filename: string }> {
  if (!["SIGNED_OFF", "IN_CLIENT_REVIEW", "CLIENT_APPROVED"].includes(rawSlot.status)) {
    throw new Error("Only finalised content ideas can be exported.");
  }

  const slot = scrubVendorTermsDeep(rawSlot);
  const idea = scrubVendorTermsDeep(rawIdea);
  const meta = scrubVendorTermsDeep(rawMeta);
  const brief = rawBrief ? scrubVendorTermsDeep(rawBrief) : rawBrief;
  const body = brief?.body ?? null;
  const brand = meta.brandName?.trim() || "the client";

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const containsArabic = hasArabic(JSON.stringify({ slot, idea, meta, brief }));
  const fontsPromise = ensureBrandFonts(doc);
  const imagesPromise = loadBriefImages(brief);
  const markPromise = loadMark();
  if (containsArabic) await ensureArabicFont(doc);
  const [fonts, images, mark] = await Promise.all([fontsPromise, imagesPromise, markPromise]);

  const title = clean(slot.title) || clean(body?.title) || idea.title;
  doc.setProperties({
    title: `${title} - creative brief`,
    subject: `Creative brief for ${brand}`,
    author: "Mohtawa",
    creator: "Mohtawa Creative OS",
    keywords: "Mohtawa, creative brief, storyboard",
  });

  const ctx: Ctx = {
    doc,
    brand,
    fonts,
    tag: statusTag(brief?.status ?? null, meta.documentVersion),
    forLine: `${brief ? "Creative brief" : "Content idea"} for ${brand}`,
    mark,
    images,
    y: 0,
  };
  drawLetterhead(ctx);

  const ratioText = displayAspectRatio(slot);
  const metaParts = [dayLabel(slot.scheduledAt), `${MEDIA_TYPE_LABEL[slot.mediaType]} ${ratioText}`, clean(slot.pillarName) || null];

  if (brief && brief.kind === "VIDEO") {
    const video = brief.body as ProductionVideoBody;
    const shots = video.storyboard ?? [];
    const timings = shotTimings(shots.map((shot) => shot.duration));
    const complete = shots.length > 0 && timings.every((timing) => timing.seconds !== null);
    const total = parsedTotal(timings);
    const wide = isLandscape(ratioText) || isLandscape(idea.aspectRatio);
    const ratio = ratioOf(ratioText, wide ? [16, 9] : [9, 16]);
    const cards = shotCards(video, images);
    const asked = idea.durationSeconds && idea.durationSeconds > 0 ? idea.durationSeconds : null;
    const lengthLine = complete
      ? `${wide ? shortLength(total) : lengthWords(total)} across ${shots.length} ${shots.length === 1 ? "shot" : "shots"}${
          asked ? (Math.round(total) === asked ? ", as the idea asked." : `. The idea asked for about ${asked}.`) : "."
        }`
      : "";
    const segments: Segment[] = complete
      ? cards.map((card, index) => ({ label: card.number, seconds: timings[index]!.seconds! }))
      : [];
    const hook: Block | null = clean(video.hookVo) ? { label: "Opening hook", text: clean(video.hookVo), hook: true } : null;
    const sound: Block | null = clean(video.soundDirection) ? { label: "Sound", text: clean(video.soundDirection) } : null;
    const length: Block | null = lengthLine ? { label: "Length", text: lengthLine, timeline: segments.length > 1 ? segments : undefined } : null;
    const fromIdea: Block = { label: "From the idea", text: `${idea.title}, picked by the team on the calendar.` };
    if (complete) metaParts.push(lengthWords(total));
    if (wide) metaParts.push(`from the idea, ${idea.title}`);
    const first = cards[0];
    const keyCaption = first ? `Shot ${first.number}, the first frame ${wide ? "the audience" : `${brand}’s audience`} sees.` : "";

    if (wide) {
      writeTitle(ctx, title, metaParts.filter(Boolean).join(" · "), 26, 20, 32);
      writeHeroAcross(ctx, first?.image, first?.onScreen ?? "", ratio, keyCaption, hook, [sound, length].filter(Boolean) as Block[]);
      writeInputs(ctx, video, 30);
      const captions = captionsOf(video, "VIDEO");
      writeStoryboard(ctx, cards, true, ratio, `${shots.length} shots${complete ? ` · ${shortLength(total)}` : ""}`);
      writeCaptions(ctx, captions, 18);
    } else {
      writeTitle(ctx, title, metaParts.filter(Boolean).join(" · "), 30, 24, 34);
      writeHeroBeside(ctx, first?.image, first?.onScreen ?? "", ratio, keyCaption, [hook, sound, length, fromIdea].filter(Boolean) as Block[]);
      writeInputs(ctx, video, 30);
      const captions = captionsOf(video, "VIDEO");
      const fits = ctx.y + captionsHeight(doc, captions, 26) <= CONTENT_BOTTOM;
      if (fits) writeCaptions(ctx, captions, 26);
      newPage(ctx);
      writeStoryboard(ctx, cards, false, ratio, `${shots.length} ${shots.length === 1 ? "shot" : "shots"}${complete ? ` · ${lengthWords(total)}` : ""}`);
      if (!fits) writeCaptions(ctx, captions, 26);
    }
    writeClosing(ctx, video);
  } else if (brief) {
    const design = brief.body as ProductionStaticBody;
    const panels = design.panels ?? [];
    const ratio = ratioOf(panels[0]?.aspectRatio || ratioText, [4, 5]);
    const cards = slideCards(design, images);
    const format = staticFormatLine(panels);
    if (panels.length) metaParts.push(`${panels.length} ${panels.length === 1 ? "slide" : "slides"}`);
    writeTitle(ctx, title, metaParts.filter(Boolean).join(" · "), 30, 24, 34);
    const blocks: Block[] = [];
    if (clean(design.headline)) blocks.push({ label: "Headline on the artwork", text: clean(design.headline), hook: true });
    if (format) blocks.push({ label: "Format", text: format });
    const size = staticSizeLine(panels);
    if (size) blocks.push({ label: "Size", text: size });
    blocks.push({ label: "From the idea", text: `${idea.title}, picked by the team on the calendar.` });
    const first = cards[0];
    writeHeroBeside(ctx, first?.image, first?.onScreen ?? "", ratio, first ? `Slide 1, the cover ${brand}’s audience sees first.` : "", blocks);
    writeInputs(ctx, design, 30);
    const art = design.artDirection;
    const captions = captionsOf(design, "STATIC");
    newPage(ctx);
    writeStoryboard(ctx, cards, false, ratio, `${panels.length} ${panels.length === 1 ? "slide" : "slides"}`);
    if (art) {
      writeSimpleList(
        ctx,
        "Art direction",
        [
          cleanList(art.palette).length ? `Palette: ${cleanList(art.palette).join(", ")}` : "",
          clean(art.typography) ? `Typography: ${clean(art.typography)}` : "",
          cleanList(art.moodWords).length ? `Mood: ${cleanList(art.moodWords).join(", ")}` : "",
          cleanList(art.doNots).length ? `Avoid: ${cleanList(art.doNots).join("; ")}` : "",
        ].filter(Boolean),
      );
    }
    writeCaptions(ctx, captions, 26);
    if (clean(design.altText)) writeSimpleList(ctx, "Alt text", [clean(design.altText)]);
    writeClosing(ctx, design);
  } else {
    // No brief yet: the approved idea alone.
    const length = idea.durationSeconds ? lengthWords(idea.durationSeconds) : null;
    if (length) metaParts.push(length);
    writeTitle(ctx, title, metaParts.filter(Boolean).join(" · "), 30, 24, 34);
    const blocks: Block[] = [{ label: "The idea", text: `${idea.title}. ${clean(idea.synopsis)}`.trim() }];
    if (clean(idea.hooks[0])) blocks.push({ label: "Opening hook", text: clean(idea.hooks[0]), hook: true });
    if (clean(idea.objectiveClaim)) blocks.push({ label: "How this idea delivers", text: clean(idea.objectiveClaim) });
    let y = ctx.y;
    blocks.forEach((block, index) => {
      if (index) y += 22;
      y = drawBlock(doc, block, PAD_X, y, CONTENT_W, 21, 10.5, 8);
    });
    ctx.y = y;
  }

  const count = doc.getNumberOfPages();
  for (let page = 1; page <= count; page += 1) {
    doc.setPage(page);
    drawFooter(doc, page, count);
  }

  const slugify = (raw: string) =>
    raw.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "creative-brief";
  const versionSlug = meta.documentVersion ? `-v${meta.documentVersion}` : "";
  const dateStamp = new Date().toISOString().slice(0, 10);
  return { doc, filename: `mohtawa-${slugify(brand)}-${slugify(title)}${versionSlug}-${dateStamp}.pdf` };
}

/** Browser action used by the product. */
export async function exportSeatBriefPdf(
  slot: CalendarPlanSlot,
  idea: CalendarSlotIdea,
  meta: SeatBriefMeta,
  brief?: SeatBriefPayload | null,
): Promise<void> {
  const { doc, filename } = await buildSeatBriefPdf(slot, idea, meta, brief);
  doc.save(filename);
}

